import type { SelectedFile } from "./SelectedFile";
import type {
  JobSeekerProfileFile,
  JobSeekerProfileResumeType,
} from "../../../../__generated__/types.gqlgenerated";

import { PARSING_RESUME_ERROR_MESSAGE } from "./resumeUploadErrors";
import { useS3Upload } from "./useS3Upload";
import { useLocale, useSafeI18n } from "../../../I18n";
import {
  type AddJobSeekerProfileResumeFileMutation,
  useAddJobSeekerProfileResumeFileMutation,
} from "../graphql/__generated__/addResumeFile.gqlgenerated";
import { useGetJobSeekerProfileUploadResumeUrlMutation } from "../graphql/__generated__/getResumeUploadUrl.gqlgenerated";

import { type FetchResult, ApolloError } from "@apollo/client";
import React from "react";

type UseResumeFileUploadOptions = {
  refetchQueries?: NonNullable<
    Parameters<typeof useAddJobSeekerProfileResumeFileMutation>[0]
  >["refetchQueries"];
};

export interface StagedResumeFile {
  /** File metadata and bytes retained by the client until the upload is committed. */
  file: SelectedFile;
  /** ID of the file uploaded to temporary storage. */
  fileId: string;
}

const PARSE_FAILED_TAG = "parse:failed";
const hasParseFailedTag = (tags: readonly string[] | undefined | null): boolean =>
  !!tags?.includes(PARSE_FAILED_TAG);

export const isUnparseableFile = (fileInformation: JobSeekerProfileFile | undefined) =>
  hasParseFailedTag(fileInformation?.tags);

// Twin of isUnparseableFile for resolved resume shapes that are not JobSeekerProfileFile.
export const isUnparseableResume = (resume: { tags?: readonly string[] } | undefined | null) =>
  hasParseFailedTag(resume?.tags);

enum UploadStage {
  IDLE = "IDLE",
  GETTING_URL = "GETTING_URL",
  UPLOADING_TO_S3 = "UPLOADING_TO_S3",
  LINKING_UPLOAD = "LINKING_UPLOAD",
  COMPLETE = "COMPLETE",
}

/**
 * Calculates a user-friendly progress percentage using both the graphql calls and upload together.
 * Progress breakdown:
 * - IDLE: 0%
 * - GETTING_URL: 10%
 * - UPLOADING_TO_S3: 25% + (s3Progress * 0.65) - pro-rated between 25% and 90%
 * - LINKING_UPLOAD: 90%
 * - COMPLETE: 100%
 */
function calculateProgress(stage: UploadStage, s3Progress: number): number {
  switch (stage) {
    case UploadStage.IDLE:
      return 0;
    case UploadStage.GETTING_URL:
      return 10;
    case UploadStage.UPLOADING_TO_S3:
      // Pro-rate S3 progress between 25% and 90%
      return 25 + (s3Progress / 100) * 65;
    case UploadStage.LINKING_UPLOAD:
      return 90;
    case UploadStage.COMPLETE:
      return 100;
    default:
      return 0;
  }
}

export function useResumeFileUpload({ refetchQueries = [] }: UseResumeFileUploadOptions = {}) {
  const { uploadFileToS3, loading: uploadLoading, progress: s3Progress } = useS3Upload();
  const [uploadStage, setUploadStage] = React.useState<UploadStage>(UploadStage.IDLE);
  const [getUrlMutation, { loading: getUrlMutationLoading }] =
    useGetJobSeekerProfileUploadResumeUrlMutation();
  const [addFileMutation, { loading: addFileMutationLoading }] =
    useAddJobSeekerProfileResumeFileMutation({
      refetchQueries,
      awaitRefetchQueries: true,
      errorPolicy: "all",
    });
  const { gettext } = useSafeI18n();
  const { country } = useLocale();
  const countryCode = country?.toUpperCase() ?? "UNKNOWN";

  const progress = React.useMemo(
    () => calculateProgress(uploadStage, s3Progress),
    [uploadStage, s3Progress],
  );

  const resetProgress = React.useCallback(() => {
    setUploadStage(UploadStage.IDLE);
  }, []);

  const stageResumeFile = React.useCallback(
    async (selectedFile: SelectedFile): Promise<StagedResumeFile> => {
      try {
        setUploadStage(UploadStage.GETTING_URL);

        const { data } = await getUrlMutation();
        const uploadUrl = data?.createJobSeekerProfileUploadResumeFileTempUrl?.uploadURL;
        const fileId = data?.createJobSeekerProfileUploadResumeFileTempUrl?.fileId;

        if (!uploadUrl || !fileId) {
          throw new Error("Failed to get upload URL and file ID");
        }

        setUploadStage(UploadStage.UPLOADING_TO_S3);
        await uploadFileToS3(uploadUrl, selectedFile);

        return { file: selectedFile, fileId };
      } catch (error) {
        console.error("[useResumeFileUpload] Upload failed:", error);
        setUploadStage(UploadStage.IDLE);
        throw error;
      }
    },
    [getUrlMutation, uploadFileToS3],
  );

  const commitStagedResumeFile = React.useCallback(
    async (stagedResumeFile: StagedResumeFile, resumeType?: JobSeekerProfileResumeType) => {
      try {
        const { file: selectedFile, fileId } = stagedResumeFile;

        setUploadStage(UploadStage.LINKING_UPLOAD);
        return addFileMutation({
          variables: {
            input: {
              fileId,
              geoCountry: countryCode,
              ipCountry: countryCode,
              mediaType: selectedFile.mimeType,
              name: selectedFile.name,
              description: gettext("Resume file uploaded from Indeed Profile"),
              resumeType,
            },
          },
        }).then((result: FetchResult<AddJobSeekerProfileResumeFileMutation>) => {
          /*
                      Manually checking the mutation result to ignore the resume parsing error and
                      return the partial data from a successfully uploaded file that includes parse failed tags
                      This approach allows the pages to display the file card with appropriate unparseable file messages
                    */
          if (
            result.errors?.length &&
            result.errors?.some((error) => !error.message.includes(PARSING_RESUME_ERROR_MESSAGE))
          ) {
            // Forwarding manually the errors in case ANY of them is not a resume parsing error
            const apolloError = new ApolloError({
              graphQLErrors: result.errors,
            });
            throw apolloError;
          }
          if (!result.data?.addJobSeekerProfileResumeFile?.originalResumeFile?.id) {
            throw new Error("uploadFile mutation call did not return an originalResumeFile");
          }

          setUploadStage(UploadStage.COMPLETE);
          return result;
        });
      } catch (error) {
        console.error("[useResumeFileUpload] Upload failed:", error);
        setUploadStage(UploadStage.IDLE);
        throw error;
      }
    },
    [addFileMutation, countryCode, gettext],
  );

  const uploadResumeFile = React.useCallback(
    async (selectedFile: SelectedFile, resumeType?: JobSeekerProfileResumeType) => {
      const stagedResumeFile = await stageResumeFile(selectedFile);
      return commitStagedResumeFile(stagedResumeFile, resumeType);
    },
    [commitStagedResumeFile, stageResumeFile],
  );

  return {
    uploadResumeFile,
    stageResumeFile,
    commitStagedResumeFile,
    resetProgress,
    loading: getUrlMutationLoading || addFileMutationLoading || uploadLoading,
    progress,
  };
}
